import {
  FetchedContent,
  MediaResolutionInput,
  MediaResolutionResult,
  MediaResolver,
  ResolverContext,
  ResolvedMedia,
} from '../types';
import { HttpContentFetcher } from '../services/HttpContentFetcher';
import { MediaResolutionCache } from '../services/MediaResolutionCache';
import { ResolverRegistry } from './ResolverRegistry';

const buildCacheKey = (input: MediaResolutionInput): string =>
  JSON.stringify({
    url: input.url.trim(),
    thumbnailUrl: input.thumbnailUrl?.trim() ?? null,
    title: input.title ?? null,
  });

const sanitizeMediaUrl = (url?: string): string | undefined => {
  const trimmed = url?.trim();

  return trimmed || undefined;
};

const normalizeResolvedMedia = (media: ResolvedMedia): ResolvedMedia => ({
  ...media,
  playbackUrl: sanitizeMediaUrl(media.playbackUrl),
  embedUrl: sanitizeMediaUrl(media.embedUrl),
  thumbnailUrl: sanitizeMediaUrl(media.thumbnailUrl),
});

const createContext = (
  input: MediaResolutionInput,
  fetcher: HttpContentFetcher,
  contentCache: Map<string, FetchedContent>
): ResolverContext => ({
  input,
  fetchContent: async (url: string) => {
    const cached = contentCache.get(url);

    if (cached) {
      return cached;
    }

    const content = await fetcher.fetchDocument(url);

    contentCache.set(url, content);

    return content;
  },
  getCachedContent: (url: string) => contentCache.get(url),
});

export class MediaResolutionPipeline {
  constructor(
    private readonly registry: ResolverRegistry,
    private readonly fetcher: HttpContentFetcher,
    private readonly cache: MediaResolutionCache
  ) {}

  async resolve(input: MediaResolutionInput): Promise<MediaResolutionResult> {
    const cacheKey = buildCacheKey(input);
    const cached = this.cache.get(cacheKey);

    if (cached) {
      return {
        input,
        media: cached,
        errors: [],
      };
    }

    const contentCache = new Map<string, FetchedContent>();
    const context = createContext(input, this.fetcher, contentCache);
    const errors: MediaResolutionResult['errors'] = [];

    for (const resolver of this.registry.getAll()) {
      try {
        const canResolve = await resolver.canResolve(context);

        if (!canResolve) {
          continue;
        }

        const resolved = await resolver.resolve(context);

        if (resolved) {
          const normalizedMedia = normalizeResolvedMedia(resolved);

          this.cache.set(cacheKey, normalizedMedia);

          return {
            input,
            media: normalizedMedia,
            errors,
          };
        }
      } catch (error) {
        errors.push({
          resolverName: resolver.name,
          message: error instanceof Error ? error.message : 'Unknown resolver error',
          cause: error,
        });
      }
    }

    const fallback: ResolvedMedia = {
      mediaType: 'video',
      playerType: 'html5',
      thumbnailUrl: input.thumbnailUrl ?? undefined,
      title: input.title ?? undefined,
      description: input.description ?? undefined,
      resolved: false,
      resolverName: 'Fallback',
      metadata: {
        sourceUrl: input.url,
      },
    };

    return {
      input,
      media: fallback,
      errors,
    };
  }

  registerResolver(resolver: MediaResolver): void {
    this.registry.register(resolver);
  }
}
